import type { ReactNode } from 'react';
import type { PublicRoomSnapshot } from '@sincronia/shared';
import { useT } from '../../i18n/index.js';
import { cn } from '../../lib/ui.js';
import { useGameStore, type ConnectionStatus } from '../../store/gameStore.js';

function Pip({ filled, tone }: { filled: boolean; tone: 'life' | 'star' }): ReactNode {
  const color = tone === 'life' ? 'var(--color-life)' : 'var(--color-star)';
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden="true">
      {tone === 'life' ? (
        <path
          d="M12 20.5S3.5 14.9 3.5 9.4A4.9 4.9 0 0 1 12 6.3a4.9 4.9 0 0 1 8.5 3.1c0 5.5-8.5 11.1-8.5 11.1z"
          fill={filled ? color : 'transparent'}
          stroke={color}
          strokeWidth="1.6"
          strokeOpacity={filled ? 1 : 0.45}
        />
      ) : (
        <path
          d="M12 3l2.7 6.1 6.6.6-5 4.4 1.5 6.5L12 17.3 6.2 20.6l1.5-6.5-5-4.4 6.6-.6L12 3z"
          fill={filled ? color : 'transparent'}
          stroke={color}
          strokeWidth="1.4"
          strokeOpacity={filled ? 1 : 0.45}
        />
      )}
    </svg>
  );
}

function ConnectionDot({ status }: { status: ConnectionStatus }): ReactNode {
  const t = useT();
  const label =
    status === 'connected'
      ? t('common.online')
      : status === 'connecting'
        ? t('common.reconnecting')
        : t('common.offline');

  return (
    <span className="flex items-center gap-1.5" title={label} data-testid="connection-status">
      <span
        aria-hidden="true"
        className={cn(
          'size-2 rounded-full',
          status === 'connected'
            ? 'bg-pulse-500'
            : status === 'connecting'
              ? 'animate-pulse bg-star'
              : 'bg-life',
        )}
      />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function TopBar({ snapshot }: { snapshot: PublicRoomSnapshot }): ReactNode {
  const t = useT();
  const connection = useGameStore((state) => state.connection);

  return (
    <header className="safe-top safe-x flex items-center justify-between gap-3 px-3 pb-2">
      <div className="flex flex-col">
        <span className="text-[10px] tracking-widest text-night-400 uppercase">
          {t('game.level')}
        </span>
        <span
          className="tabular text-lg leading-none font-semibold text-night-200"
          data-testid="level"
        >
          {snapshot.level}
          <span className="text-night-400">/{snapshot.maxLevel}</span>
        </span>
      </div>

      <div className="flex items-center gap-3">
        <div
          className="flex items-center gap-0.5"
          aria-label={`${t('game.lives')}: ${snapshot.lives}`}
          data-testid="lives"
        >
          {Array.from({ length: snapshot.maxLives }, (_, index) => (
            <Pip key={index} filled={index < snapshot.lives} tone="life" />
          ))}
          <span className="tabular ml-1 text-sm font-medium text-night-300">{snapshot.lives}</span>
        </div>
        <div
          className="flex items-center gap-0.5"
          aria-label={`${t('game.stars')}: ${snapshot.stars}`}
          data-testid="stars"
        >
          {Array.from({ length: snapshot.maxStars }, (_, index) => (
            <Pip key={index} filled={index < snapshot.stars} tone="star" />
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <ConnectionDot status={connection} />
        <span
          className="tabular rounded-lg bg-night-800/70 px-2 py-1 text-xs tracking-widest text-night-300"
          aria-label={`${t('game.room')} ${snapshot.code}`}
          data-testid="room-code-compact"
        >
          {snapshot.code}
        </span>
      </div>
    </header>
  );
}
